import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from '@emotion/styled';

import { outputIfNoVariables } from '../../utilities/misc';
import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';
import { containerStyles } from '../../styles/layout';
import { headers, body, linkStyles } from '../../styles/type';

import Button from '../buttons/Button';
import ButtonText from '../buttons/ButtonText';
import Image from '../Image';
import TextRenderer from '../TextRenderer';

const Container = styled.div`
  position: relative;
  z-index: 0;
  padding: ${rem(32)} 0;
  background-color: ${({ backgroundColor }) => (backgroundColor === 'Dark'
    ? colors.blue['800'] : colors.gray['50']
  )};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    background-color: ${colors.blue['800']};
  `}

  ${({ boxed }) => boxed && `
    background: ${colors.white};
  `}

  ${media('>tiny')} {
    padding: ${rem(56)} 0 ${rem(64)};
  }
`;

const BackgroundImage = styled(GatsbyImage)`
  position: absolute;
  z-index: -1;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.1;
  mix-blend-mode: luminosity;
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
`;

const Subhead = styled.p`
  ${headers.h6}
  margin-bottom: ${rem(12)};
  text-align: center;
  color: ${colors.blue['500']};
`;

const Headline = styled.h2`
  ${headers.h3}
  max-width: ${rem(960)};
  margin: 0 auto ${rem(48)};
  text-align: center;
  color: ${({ backgroundColor }) => (backgroundColor === 'Dark'
    ? colors.gray['100'] : colors.blue['800']
  )};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `}
`;

const Grid = styled.ul`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  --column-gap: ${rem(40)};
  --row-gap: ${rem(40)};
  gap: var(--row-gap) var(--column-gap);

  ${media('>tiny')} {
    --column-gap: ${rem(48)};
    --row-gap: ${rem(64)};
  }

  ${({ boxed }) => boxed && `

    ${media('>tiny')} {
      gap: ${rem(40)};
    }
  `}
`;

const getFlexAndMaxWidth = (columns) => `
  flex: 1 1 calc((100% - (${columns - 1} * var(--column-gap))) / ${columns});
  max-width: calc((100% - (${columns - 1} * var(--column-gap))) / ${columns});
`;

const Item = styled.li`
  flex: 1 1 100%;
  display: flex;
  flex-direction: column;
  align-items: start;
  color: ${({ backgroundColor }) => (backgroundColor === 'Dark'
    ? colors.gray['100'] : colors.blue['800']
  )};

  ${({ columns }) => columns === '3' && `
    ${media('>tiny')} {
      ${getFlexAndMaxWidth(2)}
    }

    ${media('>small')} {
      ${getFlexAndMaxWidth(3)}
    }
  `}

  ${({ columns }) => columns === '4' && `
    ${media('>tiny')} {
      ${getFlexAndMaxWidth(2)}
    }

    ${media('>small')} {
      ${getFlexAndMaxWidth(3)}
    }

    ${media('>smedium')} {
      ${getFlexAndMaxWidth(4)}
    }
  `}

  ${({ alignment }) => alignment === 'Center' && `
    align-items: center;
    text-align: center;
  `}

  ${({ boxed }) => boxed && `
    padding: ${rem(32)} ${rem(24)};
    border-radius: ${rem(8)};
    background: ${colors.blue['50']};
    color: ${colors.blue['600']};
  `}

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `}

  ${ButtonText} {
    margin-top: auto;
    padding-top: ${rem(16)};
  }
`;

const ItemIcon = styled(Image)`
  margin-bottom: ${rem(16)};
  height: ${rem(50)};
  width: auto;
`;

const ItemTitle = styled.h3`
  ${headers.h5}
`;

const ItemText = styled.div`
  ${body.regular}
  ${linkStyles}
  margin-top: ${rem(8)};
  color: ${colors.gray['600']};

  ${({ backgroundColor }) => backgroundColor === 'Dark' && `
    color: ${colors.blue['100']};
  `}

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.gray['400']};
  `}
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${rem(40)};
`;

const IconColumns = ({
  subhead,
  headline,
  iconItems,
  ctaText,
  ctaLink,
  backgroundImage,
  backgroundColor,
  columns,
  boxed,
  alignment
}) => (
  <Container backgroundColor={backgroundColor} boxed={boxed}>
    {backgroundImage && (
      <BackgroundImage
        image={getImage(backgroundImage)}
        alt={backgroundImage.description}
        role="presentation"
        aria-hidden="true"
      />
    )}
    <ContentWrapper>
      {subhead && (
        <Subhead>
          <TextRenderer text={subhead} />
        </Subhead>
      )}
      {headline && (
        <Headline backgroundColor={backgroundColor}>
          <TextRenderer text={headline} />
        </Headline>
      )}
      <Grid boxed={boxed}>
        {iconItems?.map((iconItem, index) => (
          <Item
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            backgroundColor={backgroundColor}
            alignment={alignment}
            boxed={boxed}
            columns={columns}
          >
            {iconItem.image && (
              <ItemIcon
                image={iconItem.image}
                alt={iconItem.image.description}
                role={!iconItem.image.description ? 'presentation' : null}
                aria-hidden={!iconItem.image.description ? 'true' : null}
              />
            )}
            {iconItem.headline && (
              <ItemTitle>
                <TextRenderer text={iconItem.headline} />
              </ItemTitle>
            )}
            {iconItem.text && (
              <ItemText backgroundColor={backgroundColor} light={backgroundColor === 'Dark'}>
                <TextRenderer text={iconItem.text} richText />
              </ItemText>
            )}
            {iconItem.ctaLink && iconItem.ctaText && (
              <ButtonText
                to={iconItem.ctaLink}
                aria-label={outputIfNoVariables(`${iconItem.headline} — ${iconItem.ctaText}`)}
                color={backgroundColor === 'Dark' ? 'light' : 'dark'}
                arrow={!boxed}
              >
                <TextRenderer text={iconItem.ctaText} />
              </ButtonText>
            )}
          </Item>
        ))}
      </Grid>
      {ctaLink && ctaText && (
        <ButtonContainer>
          <Button
            to={ctaLink}
            aria-label={outputIfNoVariables(`${headline} — ${ctaText}`)}
          >
            <TextRenderer text={ctaText} />
          </Button>
        </ButtonContainer>
      )}
    </ContentWrapper>
  </Container>
);

IconColumns.propTypes = {
  subhead: PropTypes.string,
  headline: PropTypes.string,
  iconItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      image: PropTypes.shape({
        description: PropTypes.string,
        // eslint-disable-next-line react/forbid-prop-types
        gatsbyImageData: PropTypes.object
      }),
      headline: PropTypes.string,
      text: PropTypes.shape({
        raw: PropTypes.string
      }),
      ctaText: PropTypes.string,
      ctaLink: PropTypes.string
    })
  ),
  backgroundImage: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    gatsbyImageData: PropTypes.object
  }),
  // TODO: Refactor to use Blue and White like all other blocks.
  backgroundColor: PropTypes.oneOf(['Light', 'Dark']).isRequired,
  columns: PropTypes.string,
  alignment: PropTypes.string,
  boxed: PropTypes.bool,
  ctaText: PropTypes.string,
  ctaLink: PropTypes.string
};

export const query = graphql`
  fragment IconColumnsFragment on ContentfulIconColumns {
    id
    __typename
    subhead
    headline
    iconItems {
      id
      image {
        gatsbyImageData(height: 50, quality: 75)
        ...ImageAssetFragment
      }
      headline
      text {
        raw
      }
      ctaText
      ctaLink
    }
    boxed
    alignment
    ctaText
    ctaLink
    backgroundImage {
      gatsbyImageData(layout: FULL_WIDTH, quality: 75)
      description
    }
    backgroundColor
    columns
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default IconColumns;
